import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import {
  bookingPaymentRequestList,
  BookingPaymentRequestListItem,
} from "@/app/Bookings/GraphQL/bookingPaymentRequestList";
import { useBookingPaymentRequestActions } from "@/app/Bookings/Hooks/useBookingPaymentRequestActions";
import { Badge } from "@/components/catalyst/badge";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { bookingPaymentRequestStatusColorMap } from "@/app/Bookings/Utils/bookingPaymentRequestStatusColorMap";
import { VariablesOf } from "@graphql-typed-document-node/core";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { ReactNode } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/catalyst/button";
import { BookingPaymentRequestStatus } from "@/gql/graphql";
import { CheckIcon } from "@heroicons/react/20/solid";
import { MailIcon } from "lucide-react";
import { CellContext } from "@/lib/Components/DataTable/types";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingPaymentRequestResolveDialog } from "@/app/Bookings/Components/BookingPaymentRequestResolveDialog";
import { sendBookingPaymentRequestMutation } from "@/app/Bookings/GraphQL/bookingPaymentRequestMutations";
import { toast } from "sonner";

type BookingPaymentRequestColumnId =
  | "id"
  | "status"
  | "amount"
  | "created_at"
  | "description"
  | "booking"
  | "supplier"
  | "actions";

export function BookingPaymentRequestTable({
  id,
  queryVariables,
  rightButtons,
}: {
  id: TableId;
  queryVariables?: Partial<VariablesOf<typeof bookingPaymentRequestList>>;
  rightButtons?: ReactNode;
}) {
  const t = useTranslations("bookingPaymentRequest");
  const getActions = useBookingPaymentRequestActions();
  const columns: DataTableColDef<
    BookingPaymentRequestListItem,
    any,
    BookingPaymentRequestColumnId
  >[] = [
    {
      id: "booking",
      header: "Booking",
      cell: ({ row }) => (
        <ModelLinkCell
          to="/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
          params={(prev) => ({ ...prev, bookingId: row.original.booking.id })}
          viewTransition
        >
          {row.original.booking.reference}
        </ModelLinkCell>
      ),
    },
    {
      id: "supplier",
      header: "Supplier",
      cell: ({ row }) => (
        <div className="text-gray-500">
          {row.original.booking.supplier.name}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          color={bookingPaymentRequestStatusColorMap[row.original.status]}
          className="capitalize"
        >
          {t(`status.${row.original.status}`)}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessorFn: (row) => formatCurrency(row.amount, row.currency),
    },
    {
      id: "description",
      header: "Request",
      cell: ({ row }) => (
        <div
          className="text-gray-500"
          dangerouslySetInnerHTML={{
            __html: row.original.description ?? "--",
          }}
        />
      ),
    },
    {
      id: "created_at",
      header: "Created",
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
    {
      id: "actions",
      cell: ActionCell,
    },
  ];

  return (
    <DataTable
      title="Payment requests"
      rightButtons={rightButtons}
      columns={columns}
      document={bookingPaymentRequestList}
      searchable={false}
      columnToggles={false}
      getQueryVariables={({ pagination }) => {
        return {
          page: pagination.pageIndex,
          first: pagination.pageSize,
          ...queryVariables,
        };
      }}
      accessor={(data) => data.bookingPaymentRequests}
      id={id}
      getActions={getActions}
    />
  );
}

function ActionCell({ row }: CellContext<BookingPaymentRequestListItem, any>) {
  const { open } = useDialog(BookingPaymentRequestResolveDialog);
  const { mutateAsync: sendEmail, isPending } = useGqlMutation(
    sendBookingPaymentRequestMutation,
  );

  return (
    <div className="flex items-center space-x-2">
      {row.original.status === BookingPaymentRequestStatus.AwaitingPayment ? (
        <Button
          plain
          LeadingIcon={MailIcon}
          isLoading={isPending}
          onClick={async () => {
            await sendEmail({
              id: row.original.id,
            });

            toast.success("Email sent");
          }}
        >
          Resend
        </Button>
      ) : null}

      {row.original.status === BookingPaymentRequestStatus.PaymentPending ? (
        <Button
          color="green"
          LeadingIcon={CheckIcon}
          onClick={() => {
            open({
              type: "capture",
              bookingPaymentRequest: row.original,
            });
          }}
        >
          Capture
        </Button>
      ) : null}
    </div>
  );
}
